/**
 * Loads PMG ingestion existence for the current order on mount.
 *
 * @module usePmgIngestionExists
 */
import { useCallback, useEffect, useState } from "react";
import { PatientGraphService } from "../../../../api/services/patientGraph/patientGraphService";
import { PmgIngestionExistsResponse } from "../../../../api/services/patientGraph/types";
import { GeneralOrderDetails } from "../../../../data-model/medicalPaOrdersModel";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";

/** Parsed PMG existence payload used by the Medical PA three-dots menu. */
export interface ParsedPmgIngestionStatus {
  /** Patient MRN echoed by the graph service. */
  mrn: string;
  /** Organization id echoed by the graph service. */
  orgId: string;
  /** True when graph data already exists for this MRN and org. */
  exists: boolean;
  /** Parsed ingest timestamp, or null when missing or invalid. */
  ingestedAt: Date | null;
}

/** Return value of {@link usePmgIngestionExists}. */
export interface UsePmgIngestionExistsReturn {
  /** Parsed existence result; null until the first successful response. */
  status: ParsedPmgIngestionStatus | null;
  /** True while the exists request is in flight. */
  loading: boolean;
  /** Error from a failed exists request; null when idle or successful. */
  error: Error | null;
  /** Re-runs the exists request for the current MRN and org. */
  refetch: () => void;
}

/**
 * Parses an ISO timestamp string into a Date, or null when empty or invalid.
 *
 * @param value - ISO-8601 timestamp from the graph service
 * @returns Valid Date, or null
 */
const parseIsoDate = (value: string | null | undefined): Date | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Maps the graph exists JSON into a typed menu-facing status object.
 *
 * @param response - Raw exists API body
 * @returns Parsed MRN, org, exists flag, and ingest timestamp
 */
const parsePmgIngestionExistsResponse = (
  response: PmgIngestionExistsResponse,
): ParsedPmgIngestionStatus => ({
  mrn: response.mrn,
  orgId: response.org_id,
  exists: Boolean(response.exists),
  ingestedAt: parseIsoDate(response.ingested_at),
});

/**
 * True when the thrown value is an aborted fetch.
 *
 * @param error - Caught rejection
 * @returns Whether callers should ignore the error
 */
const isAbortError = (error: unknown): boolean =>
  (error instanceof DOMException || error instanceof Error) &&
  error.name === "AbortError";

/**
 * Fetches PMG ingestion existence for the order's patient on mount and on refetch.
 *
 * @param generalOrderDetails - Order snapshot used to resolve patient MRN
 * @returns Parsed status, loading flag, request error, and refetch
 */
export const usePmgIngestionExists = (
  generalOrderDetails: GeneralOrderDetails,
): UsePmgIngestionExistsReturn => {
  const [status, setStatus] = useState<ParsedPmgIngestionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const patientMrn =
    generalOrderDetails?.demographics?.patient_id ??
    generalOrderDetails?.demographics?.patient_mrn ??
    "";
  const orgId = getOrgIdForFetchExternalWorklist();

  const refetch = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!patientMrn || !orgId) {
      setStatus(null);
      setLoading(false);
      setError(null);
      return;
    }

    const abortController = new AbortController();
    setLoading(true);
    setError(null);

    const loadExists = async () => {
      try {
        const response = await PatientGraphService.checkIfPmgIngestionExists(
          patientMrn,
          orgId,
          abortController.signal,
        );
        setStatus(parsePmgIngestionExistsResponse(response));
      } catch (caught) {
        if (isAbortError(caught)) {
          return;
        }
        setStatus(null);
        setError(
          caught instanceof Error
            ? caught
            : new Error("Failed to check PMG ingestion status"),
        );
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    void loadExists();

    return () => {
      abortController.abort();
    };
  }, [patientMrn, orgId, reloadKey]);

  return { status, loading, error, refetch };
};
